import { useState, useEffect } from "react";
import moment from "moment";
import constants, { policyFolderSortOptions } from "../../../../constants";
import { uniqBy } from "lodash";
import {
  archiveOfficePolicyFolder,
  deleteOfficePolicy,
  officePolicyAction,
  reNotifyPolicy,
  useGetPolicyFolderList,
  usePolicyCategory,
} from "repositories/office-policy-management-repository";
import { getStorage, setStorage } from "utils";
import {
  getOfficeNavigationContext,
  isInOfficeNavigationContext,
  OFFICE_NAV_CONTEXT_CHANGE_EVENT,
} from "utils/officeNavigationContext";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import { sortingVariablesForCurrentPolicies } from "../tabs/constants";
const CACHE_KEY = constants.officePolicies?.cache?.myOfficePolicyFilter;

const defaultDateRange = () => ({
  fromDate: null,
  toDate: null,
});

/**
 * Hook for Compliance Dashboard policy folder listing (Current Policies tab).
 * Provides policy list, filters (office, staff, status, priority, category, date range),
 * and actions. Default sort: Last Updated (sortBy = 3) so recently updated policies show first.
 * @param {boolean} isArchive - true for archived policies list
 * @param {boolean} isActive - when false, list query is disabled (only fetch when tab is active to avoid API calls on tab switch)
 */
export default function usePolicyModuleListing(
  isArchive = false,
  isActive = true,
) {
  const {
    isOwner: isAccountOwner,
    effectiveOwnerId: accountOwnerId,
    ownerListLoading,
    isStaff,
    dataAccessValues,
  } = useAccountOwnerCommon();

  const cacheFilters = getStorage(CACHE_KEY) || {};
  const officeContext = getOfficeNavigationContext();
  const initialOfficeFilters =
    isInOfficeNavigationContext(officeContext) && officeContext?.officeId
      ? [Number(officeContext.officeId)]
      : cacheFilters?.offices || [];
  const [officePolicies, setOfficePolicies] = useState([]);
  const [appliedOfficeFilters, setAppliedOfficeFilters] = useState(
    initialOfficeFilters,
  );
  const [appliedStaffFilters, setAppliedStaffFilters] = useState(
    cacheFilters?.staff || [],
  );
  const [appliedStatusFilters, setAppliedStatusFilters] = useState(
    cacheFilters?.status || [],
  );
  const [appliedCategoryFilters, setAppliedCategoryFilters] = useState(
    cacheFilters?.categoryIds || [],
  );
  const [appliedDateRange, setAppliedDateRange] = useState(
    cacheFilters?.fromDate && cacheFilters?.toDate
      ? { fromDate: cacheFilters.fromDate, toDate: cacheFilters.toDate }
      : defaultDateRange(),
  );

  const [searchValue, setSearchValue] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const [isRenew, setIsRenew] = useState(false);
  const [officeFilter, setOfficeFilter] = useState(initialOfficeFilters);
  const [staffFilter, setStaffFilter] = useState(cacheFilters?.staff || []);
  const [statusFilter, setStatusFilter] = useState(cacheFilters?.status || []);
  const [categoryFilter, setCategoryFilter] = useState(
    cacheFilters?.categoryIds || [],
  );
  const [dateRange, setDateRange] = useState(
    cacheFilters?.fromDate && cacheFilters?.toDate
      ? { fromDate: cacheFilters.fromDate, toDate: cacheFilters.toDate }
      : defaultDateRange(),
  );

  // Default sortBy = 13 (PolicyFolderSortBy.DueAll)
  const [sortBy, setSortBy] = useState(sortingVariablesForCurrentPolicies.Category);
  const [isDescending, setIsDescending] = useState(false);
  const [dueDateFilter, setDueDateFilter] = useState(policyFolderSortOptions[0].id);
  const queryClient = useQueryClient();

  const fromDateParam = appliedDateRange?.fromDate || null;
  const toDateParam = appliedDateRange?.toDate || null;

  const startDateStr =
    fromDateParam &&
    (fromDateParam instanceof Date
      ? moment(fromDateParam).format("YYYY-MM-DD")
      : fromDateParam);
  const endDateStr =
    toDateParam &&
    (toDateParam instanceof Date
      ? moment(toDateParam).format("YYYY-MM-DD")
      : toDateParam);

  const { isLoading, data, refetch, isFetching } = useGetPolicyFolderList(
    accountOwnerId,
    appliedOfficeFilters,
    appliedStaffFilters,
    searchValue,
    isArchive,
    appliedStatusFilters,
    appliedCategoryFilters,
    startDateStr,
    endDateStr,
    sortBy,
    isDescending?2:1,
    dueDateFilter,
    isRenew,
    {
      enabled:
        isActive &&
        !!accountOwnerId &&
        (isAccountOwner || (isStaff && !!accountOwnerId)),
    },
  );

  const { data: categoryResponse } = usePolicyCategory(accountOwnerId, {
    enabled:
      !!accountOwnerId &&
      (isAccountOwner || (isStaff && dataAccessValues?.isAdmin)),
  });
  const categoryList = Array.isArray(categoryResponse?.data)
    ? categoryResponse.data
    : Array.isArray(categoryResponse)
      ? categoryResponse
      : [];

  useEffect(() => {
    if (!isLoading && data?.items) {
      setOfficePolicies(uniqBy([...data.items], "id"));
      setTotalItems(data?.pagination?.totalItems ?? data.items.length ?? 0);
    }
  }, [isLoading, data]);

  useEffect(() => {
    const officeContext = getOfficeNavigationContext();
    if (isInOfficeNavigationContext(officeContext) && officeContext?.officeId) {
      const officeId = Number(officeContext.officeId);
      setOfficeFilter([officeId]);
      setAppliedOfficeFilters([officeId]);
    }
  }, []);

  useEffect(() => {
    const syncOfficeFiltersFromContext = () => {
      const officeContext = getOfficeNavigationContext();
      if (isInOfficeNavigationContext(officeContext) && officeContext?.officeId) {
        const officeId = Number(officeContext.officeId);
        setOfficeFilter([officeId]);
        setAppliedOfficeFilters([officeId]);
        return;
      }
      const cachedFilters = getStorage(CACHE_KEY) || {};
      setOfficeFilter(cachedFilters?.offices || []);
      setAppliedOfficeFilters(cachedFilters?.offices || []);
    };
    window.addEventListener(
      OFFICE_NAV_CONTEXT_CHANGE_EVENT,
      syncOfficeFiltersFromContext,
    );
    return () =>
      window.removeEventListener(
        OFFICE_NAV_CONTEXT_CHANGE_EVENT,
        syncOfficeFiltersFromContext,
      );
  }, []);

  // Don't invalidate/refetch on mount or filter change - useQuery already fetches when enabled and refetches when queryKey (params) change

  const resetFilter = () => {
    setOfficeFilter([]);
    setStaffFilter([]);
    setStatusFilter([]);
    setCategoryFilter([]);
    setDateRange(defaultDateRange());
    setAppliedOfficeFilters([]);
    setAppliedStaffFilters([]);
    setAppliedStatusFilters([]);
    setAppliedCategoryFilters([]);
    setAppliedDateRange(defaultDateRange());
    setStorage(CACHE_KEY, {
      offices: [],
      staff: [],
      status: [],
      categoryIds: [],
      fromDate: null,
      toDate: null,
    });
  };

  const handleApplyFilters = () => {
    setAppliedOfficeFilters(officeFilter);
    setAppliedStaffFilters(staffFilter);
    setAppliedStatusFilters(statusFilter);
    setAppliedCategoryFilters(categoryFilter);
    setAppliedDateRange(
      dateRange?.fromDate && dateRange?.toDate ? dateRange : null,
    );
    setStorage(CACHE_KEY, {
      offices: officeFilter,
      staff: staffFilter,
      status: statusFilter,
      categoryIds: categoryFilter,
      fromDate: dateRange?.fromDate ?? null,
      toDate: dateRange?.toDate ?? null,
    });
  };

  const handleSearchTerm = (e) => {
    const value = e.target.value;
    setInputValue(value);
    if (value.length >= 3) {
      setSearchValue(value);
    } else if (value.length === 0) {
      setSearchValue("");
    }
  };

  const handleAction = async (officePolicyId, policyAction) => {
    try {
      const res = await officePolicyAction(officePolicyId, policyAction);
      if (res?.status) {
        toast.success(res.message);
        if (policyAction === "Cancel") {
          setOfficePolicies((prev) =>
            prev.map((p) =>
              p.id === officePolicyId ? { ...p, overallStatus: 7 } : p,
            ),
          );
        } else {
          setOfficePolicies((prev) =>
            prev.filter((p) => p.id !== officePolicyId),
          );
        }
      }
    } catch (e) {
      toast.error(e?.message);
    }
  };

  const deleteAction = async (officePolicyId) => {
    try {
      const res = await deleteOfficePolicy(officePolicyId);
      if (res?.status) {
        toast.success(res.message);
        setOfficePolicies((prev) =>
          prev.filter((p) => p.id !== officePolicyId),
        );
      }
    } catch (e) {
      toast.error(e?.message);
    }
  };

  const handleArchivePolicyFolder = async (folderIds, isArchived) => {
    const ids = Array.isArray(folderIds) ? folderIds : [folderIds];
    if (!ids.length) return;
    const params = {
      FolderIds: ids.map(Number),
      IsArchived: !!isArchived,
    };
    try {
      const res = await archiveOfficePolicyFolder(params);
      if (res?.status) {
        toast.success(res.message);
        const idsToRemove = params.FolderIds;
        setOfficePolicies((prev) =>
          prev.filter((p) => !idsToRemove.includes(p.id)),
        );
        // Refetch current tab's list so displayed data (from query) updates and archived/unarchived item is removed
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "/officePolicies/policyFolderList" &&
            query.queryKey[5] === isArchive,
        });
      }
    } catch (e) {
      toast.error(e?.message);
    }
  };

  /** Re-notify one or more policies by policy IDs. Same API for row action and bulk. */
  const renotifyAction = async (policyIdOrIds) => {
    const ids = Array.isArray(policyIdOrIds) ? policyIdOrIds : [policyIdOrIds];
    if (!ids.length) return;
    await renotifyBulkAction(ids);
  };

  /** Bulk re-notify selected policies by policy IDs. Used by global header and row Re-notify. */
  const renotifyBulkAction = async (policyIds) => {
    const ids = Array.isArray(policyIds) ? policyIds : [policyIds];
    if (!ids.length) return;
    try {
      const res = await reNotifyPolicy(ids?.map(Number));
      if (res?.status) {
        toast.success(res.message || "Re-notify sent.");
      }
    } catch (e) {
      toast.error(e?.message);
    }
  };

  const isFilterApply =
    appliedOfficeFilters?.length > 0 ||
    appliedStaffFilters?.length > 0 ||
    appliedStatusFilters?.length > 0 ||
    appliedCategoryFilters?.length > 0||(appliedDateRange?.fromDate && appliedDateRange?.toDate);

  const isAnyLoading = isLoading || isFetching || ownerListLoading;

  // Use query data directly when available so table always shows latest after refetch; fallback to state for optimistic updates
  const displayPolicies =
    data?.items != null ? uniqBy([...data.items], "id") : officePolicies;
  const displayTotalItems =
    data?.pagination?.totalItems != null
      ? data.pagination.totalItems
      : totalItems;

  return {
    isLoading: isAnyLoading,
    officePolicyLoading: isLoading,
    officePolicyFetching: isFetching,
    officePolicies: displayPolicies,
    totalItems: displayTotalItems,
    inputValue,
    accountOwnerId,
    officeFilter,
    setOfficeFilter,
    staffFilter,
    setStaffFilter,
    statusFilter,
    setStatusFilter,
    categoryFilter,
    setCategoryFilter,
    categoryList,
    dateRange,
    setDateRange,
    isFilterApply,
    sortBy,
    setSortBy,
    isDescending,
    setIsDescending,
    dueDateFilter,
    setDueDateFilter,
    setIsRenew,
    handleSearchTerm,
    handleApplyFilters,
    resetFilter,
    handleArchivePolicyFolder,
    renotifyAction,
    renotifyBulkAction,
    refetch,
    isStaff,
    appliedOfficeFilters,
    appliedStaffFilters,
    appliedStatusFilters,
    appliedCategoryFilters,
    appliedDateRange,
    searchValue,
    isRenew,
    setAppliedOfficeFilters,
    setAppliedStatusFilters,
    setAppliedCategoryFilters,
    setAppliedDateRange,
    officeDataApi: data,
  };
}
